import { Note, P, Terms } from '../prose';
import type { Topic } from '../types';

/** Reading the numbers, and producing the evidence for a single decision. */
export const auswertung = {
  auswertung: {
    group: 'auswertung',
    title: { de: 'Auswertung', en: 'Analytics' },
    teaser: {
      de: 'Was die Kennzahlen zählen — und wovon sie jeweils der Anteil sind.',
      en: 'What each figure counts — and what it is a share of.',
    },
    body: {
      de: (
        <>
          <P>
            Der Nenner entscheidet, ob eine Zahl etwas bedeutet. Ein wiederkehrender Besucher mit
            gespeicherter Entscheidung erzeugt einen Seitenaufruf und kein Banner; würde man den
            mitzählen, sähe jede gesunde Website nach einer katastrophalen Einwilligungsrate aus.
          </P>
          <Terms
            items={[
              ['Einwilligungsrate', 'Anteil der Entscheidungen, die nicht „alles ablehnen“ waren — bezogen auf Entscheidungen, nicht auf Besuche.'],
              ['Banner gezeigt', 'Wie oft das Banner ausgeliefert wurde, und wie viele unterschiedliche Besucher dahinterstehen.'],
              ['Entscheidungsquote', 'Anteil der gezeigten Banner, auf die eine Entscheidung folgte. Der Rest hat weggescrollt.'],
              ['Ø Entscheidungszeit', 'Median zwischen Erscheinen und Klick. Der p95 daneben zeigt die langsame Hälfte der Ausreißer.'],
            ]}
          />
          <Note>
            Bots sind ausgenommen. Die Aggregate entstehen beim Eintreffen der Ereignisse, nicht
            rückwirkend — ein heute eingebautes Snippet füllt keine Zahlen von letzter Woche.
          </Note>
        </>
      ),
      en: (
        <>
          <P>
            The denominator decides whether a number means anything. A returning visitor with a
            stored decision produces a pageview and no banner; counting those would make every
            healthy site look like it had a catastrophic opt-in rate.
          </P>
          <Terms
            items={[
              ['Consent rate', 'Share of decisions that were not "reject all" — against decisions, not against visits.'],
              ['Banner shown', 'How often the banner was delivered, and how many distinct visitors are behind that.'],
              ['Decision rate', 'Share of shown banners that were followed by a decision. The rest scrolled past.'],
              ['Median decision time', 'Median between appearing and being clicked. The p95 beside it shows the slow half of the outliers.'],
            ]}
          />
          <Note>
            Bots are excluded. Aggregates are built as events arrive, not retroactively — a snippet
            installed today fills in no numbers for last week.
          </Note>
        </>
      ),
    },
  },

  nachweise: {
    group: 'auswertung',
    title: { de: 'Nachweise', en: 'Proofs' },
    teaser: {
      de: 'Der Beleg für eine einzelne Einwilligung, inklusive des gezeigten Wortlauts.',
      en: 'The evidence for one consent, including the wording that was shown.',
    },
    body: {
      de: (
        <>
          <P>
            Gesucht wird mit einer Einwilligungs-ID oder einem Subjekt-Hash. Die ID kann im
            Auswahl-Dialog angezeigt werden — dann bringt eine Auskunftsanfrage sie meist gleich
            mit.
          </P>
          <P>
            Was einen Eintrag zum Nachweis macht, ist nicht die Zeile „Statistik: erlaubt“,
            sondern <strong>was gezeigt wurde</strong>: der Wortlaut, der über dem Button stand,
            als geklickt wurde. Er wird über die Fassungskennung wiederhergestellt. Art. 7 (1)
            DSGVO verlangt den Nachweis, worauf sich die Einwilligung bezog — ein Klick allein ist
            keiner.
          </P>
          <Terms
            items={[
              ['Subjekt', 'Ein Hash, keine Person. Er verbindet die Entscheidungen desselben Browsers, ohne den Besucher zu identifizieren.'],
              ['Fassung', 'Welche veröffentlichte Konfiguration galt. Die Textprüfsumme daneben belegt den Wortlaut auch dann, wenn die Fassung nicht mehr vorliegt.'],
              ['Änderung 1, 2, …', 'Widerruf und spätere Korrekturen stehen als eigene Belege in derselben Reihe.'],
            ]}
          />
          <P>
            <strong>Als JSON sichern</strong> exportiert genau das, was auf dem Bildschirm steht —
            die Datei, die eine Auskunftsanfrage beantwortet.
          </P>
        </>
      ),
      en: (
        <>
          <P>
            Search by consent ID or subject hash. The ID can be displayed in the preferences
            dialog, in which case a subject access request usually arrives carrying it.
          </P>
          <P>
            What makes an entry evidence is not the line reading "analytics: allowed" but
            <strong> what was shown</strong>: the wording that stood above the button when it was
            clicked. It is recovered through the version reference. Art. 7 (1) GDPR asks you to
            prove what the consent referred to — a click on its own is not that.
          </P>
          <Terms
            items={[
              ['Subject', 'A hash, not a person. It links decisions made by the same browser without identifying the visitor.'],
              ['Version', 'Which published configuration applied. The text checksum beside it evidences the wording even when the version itself is gone.'],
              ['Change 1, 2, …', 'Withdrawals and later corrections appear as their own records in the same series.'],
            ]}
          />
          <P>
            <strong>Save as JSON</strong> exports exactly what is on screen — the file that answers
            a subject access request.
          </P>
        </>
      ),
    },
  },

  unversehrtheit: {
    group: 'auswertung',
    title: { de: 'Unversehrtheit der Kette', en: 'Chain integrity' },
    teaser: {
      de: 'Was „lückenlos“ belegt, und was ein Bruch bedeutet.',
      en: 'What "unbroken" proves, and what a break means.',
    },
    body: {
      de: (
        <>
          <P>
            Jeder Beleg trägt eine Prüfsumme, die den vorherigen einschließt. Geprüft wird durch
            Neuberechnung derselben Prüfsummen mit demselben Code, der sie beim Schreiben erzeugt
            hat — nicht mit einer zweiten Umsetzung davon.
          </P>
          <P>
            Die Aussage ist bewusst genau: nicht „das kann nicht verändert werden“, sondern
            <strong> eine Veränderung würde hier auffallen</strong>. Die Datenbank kann gelöscht
            werden; die Kette kann das nicht verbergen.
          </P>
          <Note>
            Ein Bruch heißt nicht, dass der angezeigte Nachweis falsch ist. Er heißt, dass die
            Reihe um ihn herum nicht mehr geschlossen ist — ein fehlender Beleg und zwei Ketten für
            eine Website sind verschiedene Vorwürfe, und der genannte Grund unterscheidet sie.
          </Note>
        </>
      ),
      en: (
        <>
          <P>
            Every record carries a checksum that includes its predecessor. Verification recomputes
            those checksums with the same code that produced them on write — not with a second
            implementation of it.
          </P>
          <P>
            The claim is deliberately precise: not "this cannot be changed" but
            <strong> a change would be visible here</strong>. The database can be deleted; the
            chain cannot hide that it was.
          </P>
          <Note>
            A break does not mean the proof you are looking at is wrong. It means the series around
            it is no longer closed — a missing record and two chains for one site are different
            accusations, and the stated reason separates them.
          </Note>
        </>
      ),
    },
  },
} as const satisfies Record<string, Topic>;
